"use client";
import { useTranslations } from "next-intl";
import { ArrowUpRight, MessageSquareText } from "lucide-react";
import { Link } from "@/i18n/routing";
import { getOwnerHref } from "@/lib/utils/text";
import type { PublicListingPublisher } from "@/lib/types/search";
import OwnerLogo from "@/components/owner/OwnerLogo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import OwnerContactList from "@/app/[locale]/(public)/owner/[ownerType]/[slug]/_components/OwnerContactList";

export type PublisherContact = PublicListingPublisher & {
  emails: string[];
  phoneNumbers: string[];
  websiteUrl?: string | null;
};
export default function PublisherContactCard({
  publisher,
}: {
  publisher: PublisherContact;
}) {
  const t = useTranslations("public-publisher");
  return (
    <section className="flex flex-col gap-4" aria-label={t("contact-title")}>
      <div className="flex items-center gap-3">
        <OwnerLogo
          name={publisher.name}
          imageUrl={publisher.imageUrl}
          className="size-12"
        />
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{t("published-by")}</p>
          <p className="mt-0.5 break-words text-base font-semibold">
            {publisher.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(publisher.type === "AGENCY" ? "organization" : "individual")}
          </p>
        </div>
      </div>
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="secondary" className="h-12 w-full">
            <MessageSquareText className="size-4" />
            {t("contact-title")}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("contact-name", { name: publisher.name })}
            </DialogTitle>
            <DialogDescription>{t("contact-note")}</DialogDescription>
          </DialogHeader>
          <OwnerContactList
            name={publisher.name}
            contact={{ ...publisher, websiteUrl: publisher.websiteUrl ?? null }}
            skipHeadline
          />
        </DialogContent>
      </Dialog>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {t("direct-contact-note")}
      </p>
      {publisher.isPageVisible && (
        <Button variant="outline" asChild className="w-full">
          <Link href={getOwnerHref(publisher.type, publisher.slug)}>
            {t("view-profile")}
            <ArrowUpRight className="size-4" />
          </Link>
        </Button>
      )}
    </section>
  );
}
